import React, { ReactNode } from 'react';
import { FormattedMessage, MessageDescriptor } from 'react-intl';
import { Box } from '@tink/core/src/components/atoms/Box/Box';
import { AlertIcon, Severity } from '@tink/core/src/components/atoms/Icons/AlertIcon';
import { ErrorStatus } from '@tink/core/src/tinkLinkErrorCodes';
import { H2 } from '../../atoms/Heading/Heading';
import { OrderedBulletList } from '../../../core/atoms/OrderedBulletList/OrderedBulletList';
import { BorderContainer } from '../../atoms/Container/Container';
import { TinkErrorReason } from '../../../stores/app-state-errors';
import { DD, Details, DetailsBody, DetailsSection, DT, StyledParagraph } from './Alert.styles';
import { messages } from './messages';

/* Only use unstyled components (simple span/a/button elements). Styling will be controlled by the Alert component */
export type AlertContentType = (
  | { text: ReactNode }
  | { items: ReactNode[] }
  | { view: ReactNode }
)[];

export type AlertContentProps = {
  content: AlertContentType;
};

export const AlertContent: React.VFC<AlertContentProps> = ({ content }) => {
  return (
    <>
      {content.map((block, i) => {
        if ('text' in block) {
          // eslint-disable-next-line react/no-array-index-key
          return <StyledParagraph key={i}>{block.text}</StyledParagraph>;
        }

        if ('view' in block) {
          // eslint-disable-next-line react/no-array-index-key
          return <React.Fragment key={i}>{block.view}</React.Fragment>;
        }

        return (
          // eslint-disable-next-line react/no-array-index-key
          <OrderedBulletList size="small" key={i}>
            {block.items.map((s, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <li key={`${s}-${index}`}>{s}</li>
            ))}
          </OrderedBulletList>
        );
      })}
    </>
  );
};

type DetailsItemProps = {
  value?: string;
  descriptor: MessageDescriptor;
};

export const DetailsItem = ({ descriptor, value }: DetailsItemProps) => {
  if (!value) {
    return null;
  }

  return (
    <>
      <DT>
        <FormattedMessage {...descriptor} />
      </DT>
      <DD>{value}</DD>
    </>
  );
};

export type AlertErrorDetailsProps = {
  errorReason: TinkErrorReason;
  errorStatus: ErrorStatus;
  trackingId: string;
  credentialsDetailedErrorType?: string;
  paymentRequestId?: string;
  providerName?: string;
};

export const AlertErrorDetails = ({
  errorReason,
  errorStatus,
  credentialsDetailedErrorType,
  trackingId,
  paymentRequestId,
  providerName,
}: AlertErrorDetailsProps) => {
  return (
    <DetailsSection as="dl">
      <DetailsItem descriptor={messages.errorReason} value={errorReason} />
      <DetailsItem descriptor={messages.errorStatus} value={errorStatus} />
      <DetailsItem descriptor={messages.errorType} value={credentialsDetailedErrorType} />
      <DetailsItem descriptor={messages.errorTrackingId} value={trackingId} />
      <DetailsItem descriptor={messages.paymentRequestId} value={paymentRequestId} />
      <DetailsItem descriptor={messages.provider} value={providerName} />
    </DetailsSection>
  );
};

export type AlertProps = {
  title: ReactNode;
  severity?: Severity;
  body?: AlertContentType;
  error?: AlertErrorDetailsProps;
};

export const Alert: React.VFC<AlertProps> = ({ title, body, error, severity = Severity.ERROR }) => {
  return (
    <BorderContainer mb="xxsmall" px="normal" py="normal">
      <AlertIcon severity={severity} />
      <H2 mt="normal" as="h1">
        {title}
      </H2>
      {body && (
        <Box mt="normal">
          <AlertContent content={body} />
        </Box>
      )}
      {error && (
        <Box mt="normal">
          <Details aria-labelledby="details-summary">
            <summary id="details-summary">
              <StyledParagraph color="secondary" as="span">
                <FormattedMessage {...messages.details} />
              </StyledParagraph>
            </summary>
            <DetailsBody px="normal" py="normal" mt="normal">
              <AlertErrorDetails {...error} />
            </DetailsBody>
          </Details>
        </Box>
      )}
    </BorderContainer>
  );
};
